import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  CurrencyEuroIcon,
  ShoppingBagIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CubeIcon,
  TicketIcon,
  ReceiptPercentIcon,
  ChevronDownIcon,
  ArrowsRightLeftIcon,
  CogIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { useUIStore } from '@/stores/uiStore';
import { useDashboard } from '@/hooks/useDashboard';
import {
  useRevenueReport,
  useOrdersReport,
  useOrderIntelligenceReport,
  useCustomerIntelligenceReport,
  useProductIntelligenceReport,
  useSalesBreakdown,
  useAbandonedCarts,
  useAlerts,
  useAttributionReport,
  useProductsReport,
  useCategoriesReport,
  useCouponsReport,
  useTaxesReport,
  useStockReport,
  useCustomersReport,
  useAnalyticsSettings,
  useUpdateAnalyticsSettings,
  useSyncAnalyticsSettings,
} from '@/hooks/useAnalytics';
import { StatCard } from '@/components/ui/StatCard';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { SkeletonChart, SkeletonTable } from '@/components/ui/Skeleton';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatCurrency, formatDate, formatPercent, formatNumber, formatRelativeTime } from '@/utils/format';
import { computeDateRange } from '@/utils/dateRange';
import { cn } from '@/utils/cn';
import { ProductImage } from '@/components/ui/ProductImage';
import type { AnalyticsTrend, AnalyticsDateRangePreset, AnalyticsCompareMode, SiteAnalyticsSettings, AttributionRow } from '@wooconnect/shared';
import { DEFAULT_ANALYTICS_SETTINGS } from '@wooconnect/shared';

// ── Constants ────────────────────────────────────────────────

type TabKey = 'overview' | 'products' | 'orders' | 'customers' | 'attribution' | 'stock' | 'coupons' | 'taxes' | 'compare' | 'settings';

const STATUS_COLORS: Record<string, string> = {
  pending: '#F59E0B',
  processing: '#af7964',
  'on-hold': '#EAB308',
  completed: '#10B981',
  cancelled: '#EF4444',
  refunded: '#8B5CF6',
  failed: '#DC2626',
};

const CHART_COLORS = ['#af7964', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#F97316'];

const STOCK_COLORS: Record<string, string> = {
  instock: '#10B981',
  lowstock: '#F59E0B',
  outofstock: '#EF4444',
  onbackorder: '#8B5CF6',
};

// ── Helpers ──────────────────────────────────────────────────

function formatCompareLabel(compare: AnalyticsCompareMode, dateRange: { after: string; before: string }): string {
  const fmt = (d: Date) => `${d.getDate()}/${d.getMonth() + 1}`;
  if (compare === 'previousYear') {
    const shift = (s: string) => { const d = new Date(s.split('T')[0]!); d.setFullYear(d.getFullYear() - 1); return d; };
    return `vs ${fmt(shift(dateRange.after))}-${fmt(shift(dateRange.before))}`;
  }
  // Duration includes both endpoints: before - after + 1 day
  const afterMs = new Date(dateRange.after.split('T')[0]!).getTime();
  const beforeMs = new Date(dateRange.before.split('T')[0]!).getTime();
  const dur = beforeMs - afterMs + 86400000;
  return `vs ${fmt(new Date(afterMs - dur))}-${fmt(new Date(afterMs - 86400000))}`;
}

// ── Trend Display ────────────────────────────────────────────

function TrendBadge({ trend }: { trend: AnalyticsTrend | undefined }) {
  if (!trend || trend.trend === 'neutral') return null;
  const isUp = trend.trend === 'up';
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium', isUp ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400')}>
      {isUp ? <ArrowTrendingUpIcon className="h-3.5 w-3.5" /> : <ArrowTrendingDownIcon className="h-3.5 w-3.5" />}
      {formatPercent(trend.percentage)}
    </span>
  );
}

/** Thin proportion bar for ranked list rows — each item's share of the leader. */
function ShareBar({ fraction, tone = '#af7964' }: { fraction: number; tone?: string }) {
  const pct = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0)) * 100;
  return (
    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
      <div className="h-1 rounded-full" style={{ width: `${pct}%`, backgroundColor: tone }} />
    </div>
  );
}

type HeroIcon = React.ComponentType<{ className?: string }>;
function MetricCard({ label, value, trend, subtitle, secondaryValue, detail, loading, icon: Icon, tone }: { label: string; value: string; trend?: AnalyticsTrend; subtitle?: string; secondaryValue?: string; detail?: string; loading?: boolean; icon?: HeroIcon; tone?: string }) {
  if (loading) {
    return (
      <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
        <Skeleton className="mb-2 h-3 w-20" />
        <Skeleton className="mb-1 h-7 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
        {Icon && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]" style={{ backgroundColor: (tone ?? '#af7964') + '1f', color: tone ?? '#af7964' }}>
            <Icon className="h-4 w-4" />
          </span>
        )}
      </div>
      <p className="mt-1 text-2xl font-light tabular-nums text-gray-900 dark:text-gray-100">{value}</p>
      <div className="mt-0.5 flex items-center gap-1.5">
        <TrendBadge trend={trend} />
        {detail && <span className="text-[10px] text-gray-400 dark:text-gray-500">{detail}</span>}
        {subtitle && <span className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</span>}
      </div>
      {secondaryValue && <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">{secondaryValue}</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  Main Component
// ══════════════════════════════════════════════════════════════

export function AnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeSiteId = useUIStore((s) => s.activeSiteId);
  const activeSite = useUIStore((s) => s.activeSite);
  const currency = activeSite?.currencySymbol ?? '\u20AC';

  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [compare, setCompare] = useState<AnalyticsCompareMode>('previousPeriod');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCompareDropdown, setShowCompareDropdown] = useState(false);
  const [customFrom, setCustomFromRaw] = useState('');
  const [customTo, setCustomToRaw] = useState('');
  const setCustomFrom = (v: string) => {
    setCustomFromRaw(v);
    if (customTo && v > customTo) setCustomToRaw(v);
  };
  const setCustomTo = (v: string) => {
    setCustomToRaw(v);
    if (customFrom && v < customFrom) setCustomFromRaw(v);
  };

  const ORDER_STATUSES = ['pending', 'processing', 'on-hold', 'completed', 'cancelled', 'refunded', 'failed'] as const;

  const analyticsSettings = useAnalyticsSettings(activeSiteId);
  const settings = analyticsSettings.data ?? DEFAULT_ANALYTICS_SETTINGS;

  const [datePreset, setDatePreset] = useState<AnalyticsDateRangePreset>(settings.defaultPreset);
  const [userOverride, setUserOverride] = useState(false);

  // Sync datePreset when settings load or change (e.g. after save), unless user manually picked a preset
  useEffect(() => {
    if (analyticsSettings.data && !userOverride) {
      setDatePreset(analyticsSettings.data.defaultPreset);
    }
  }, [analyticsSettings.data?.defaultPreset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync compare default from settings
  useEffect(() => {
    if (analyticsSettings.data?.compareDefaultPreset) {
      setCompare(analyticsSettings.data.compareDefaultPreset);
    }
  }, [analyticsSettings.data?.compareDefaultPreset]);

  // Default status filter to paid statuses from settings
  const [statusUserOverride, setStatusUserOverride] = useState(false);
  useEffect(() => {
    if (analyticsSettings.data?.paidStatuses?.length && !statusUserOverride) {
      setSelectedStatuses(analyticsSettings.data.paidStatuses.map((s) => s.replace(/^wc-/, '')));
    }
  }, [analyticsSettings.data?.paidStatuses]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusIs = selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined;

  const dateRange = useMemo(() => {
    if (datePreset === 'custom' && customFrom && customTo) {
      const days = Math.round((new Date(customTo).getTime() - new Date(customFrom).getTime()) / 86400000);
      const interval: 'day' | 'week' | 'month' = days > 180 ? 'month' : days > 30 ? 'week' : 'day';
      return { after: `${customFrom}T00:00:00`, before: `${customTo}T23:59:59`, interval };
    }
    return computeDateRange(datePreset, settings.weekStartsOn, settings.timezone || undefined);
  }, [datePreset, settings.weekStartsOn, settings.timezone, customFrom, customTo]);
  const dateParams = useMemo(() => ({
    after: dateRange.after,
    before: dateRange.before,
    interval: dateRange.interval,
    compare,
    status_is: statusIs,
  }), [dateRange, compare, statusIs]);

  // Only fetch data for the active tab
  const revenueReport = useRevenueReport(activeSiteId, activeTab === 'overview' ? dateParams : null);
  const alertsReport = useAlerts(activeSiteId, activeTab === 'overview' ? dateParams : null);
  const ordersReport = useOrdersReport(activeSiteId, activeTab === 'orders' ? dateParams : null);
  const orderIntelReport = useOrderIntelligenceReport(activeSiteId, activeTab === 'orders' ? dateParams : null);
  const salesBreakdownReport = useSalesBreakdown(activeSiteId, activeTab === 'orders' ? dateParams : null);
  const abandonedCartsReport = useAbandonedCarts(activeSiteId, activeTab === 'orders' ? dateParams : null);
  const productsReport = useProductsReport(activeSiteId, activeTab === 'products' ? dateParams : null);
  const productIntelReport = useProductIntelligenceReport(activeSiteId, activeTab === 'products' ? dateParams : null);
  const categoriesReport = useCategoriesReport(activeSiteId, activeTab === 'products' ? dateParams : null);
  const couponsReport = useCouponsReport(activeSiteId, activeTab === 'coupons' ? dateParams : null);
  const couponsRevenue = useRevenueReport(activeSiteId, activeTab === 'coupons' ? dateParams : null);
  const taxesReport = useTaxesReport(activeSiteId, activeTab === 'taxes' ? dateParams : null);
  const stockReport = useStockReport(activeTab === 'stock' ? activeSiteId : null);
  const customersReport = useCustomersReport(activeSiteId, activeTab === 'customers' ? dateParams : null);
  const customerIntelReport = useCustomerIntelligenceReport(activeSiteId, activeTab === 'customers' ? dateParams : null);
  const attributionReport = useAttributionReport(activeSiteId, activeTab === 'attribution' ? dateParams : null);
  const { data: dashboardData } = useDashboard(activeSiteId);
  // Revenue figures are always net (excl. VAT) from wc_order_stats.net_total
  const taxIncl = dashboardData?.stats?.taxDisplayShop === 'incl';

  if (!activeSiteId) {
    return <EmptyState icon={ChartBarIcon} title={t('dashboard.selectStore')} />;
  }

  const tabs: Array<{ key: TabKey; label: string; icon: typeof ChartBarIcon }> = [
    { key: 'overview', label: t('analytics.overview'), icon: ChartBarIcon },
    { key: 'products', label: t('analytics.products'), icon: CubeIcon },
    { key: 'orders', label: t('analytics.orders'), icon: ShoppingBagIcon },
    { key: 'customers', label: t('analytics.customers'), icon: UsersIcon },
    { key: 'attribution', label: t('analytics.attribution'), icon: ArrowTrendingUpIcon },
    { key: 'stock', label: t('analytics.stock'), icon: ExclamationTriangleIcon },
    { key: 'coupons', label: t('analytics.coupons'), icon: TicketIcon },
    { key: 'taxes', label: t('analytics.taxes'), icon: ReceiptPercentIcon },
    { key: 'compare', label: t('analytics.compare'), icon: ArrowsRightLeftIcon },
    { key: 'settings', label: t('analytics.settings'), icon: CogIcon },
  ];

  const datePresets: Array<{ key: AnalyticsDateRangePreset; label: string }> = [
    { key: 'today', label: t('analytics.today') },
    { key: 'yesterday', label: t('analytics.yesterday') },
    { key: 'last7days', label: t('analytics.last7days') },
    { key: 'last14days', label: t('analytics.last14days') },
    { key: 'last30days', label: t('analytics.last30days') },
    { key: 'last90days', label: t('analytics.last90days') },
    { key: 'last365days', label: t('analytics.last365days') },
    { key: 'thisWeek', label: t('analytics.thisWeek') },
    { key: 'lastWeek', label: t('analytics.lastWeek') },
    { key: 'thisMonth', label: t('analytics.thisMonth') },
    { key: 'lastMonth', label: t('analytics.lastMonth') },
    { key: 'thisQuarter', label: t('analytics.thisQuarter') },
    { key: 'thisYear', label: t('analytics.thisYear') },
    { key: 'custom', label: t('analytics.customRange') },
  ];

  const currentPresetLabel = datePreset === 'custom'
    ? (customFrom && customTo ? `${formatDate(customFrom, 'dd/MM')} - ${formatDate(customTo, 'dd/MM')}` : t('analytics.customRange'))
    : datePresets.find((p) => p.key === datePreset)?.label ?? t('analytics.last30days');

  return (
    <div className="space-y-6">
      {/* Header with date range */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('analytics.title')}</h1>
          <p className="text-xs text-gray-400 dark:text-gray-500">{formatDate(dateRange.after.split('T')[0]!, 'dd MMM yyyy')} - {formatDate(dateRange.before.split('T')[0]!, 'dd MMM yyyy')}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date range dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowDateDropdown(!showDateDropdown)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {currentPresetLabel}
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            {showDateDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowDateDropdown(false)} />
                <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {datePresets.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => { setDatePreset(p.key); setUserOverride(true); setShowDateDropdown(false); }}
                      className={cn(
                        'block w-full px-4 py-2 text-left text-sm transition-colors',
                        datePreset === p.key
                          ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700',
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {/* Custom date inputs */}
          {datePreset === 'custom' && (
            <div className="flex items-center gap-2">
              <DatePicker value={customFrom} onChange={setCustomFrom} compact />
              <span className="text-gray-400">-</span>
              <DatePicker value={customTo} onChange={setCustomTo} min={customFrom} compact />
            </div>
          )}
          {/* Status filter dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700',
                selectedStatuses.length > 0
                  ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-600 dark:bg-brand-900/20 dark:text-brand-400'
                  : 'border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
              )}
            >
              {selectedStatuses.length > 0
                ? `${t('analytics.statusFilter')} (${selectedStatuses.length})`
                : t('analytics.allStatuses')}
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            {showStatusDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowStatusDropdown(false)} />
                <div className="absolute right-0 z-20 mt-1 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  <button
                    onClick={() => { setSelectedStatuses([]); setStatusUserOverride(true); }}
                    className={cn(
                      'block w-full px-4 py-2 text-left text-sm transition-colors',
                      selectedStatuses.length === 0
                        ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                        : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700',
                    )}
                  >
                    {t('analytics.allStatuses')}
                  </button>
                  <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                  {ORDER_STATUSES.map((status) => {
                    const isChecked = selectedStatuses.includes(status);
                    return (
                      <button
                        key={status}
                        onClick={() => {
                          setStatusUserOverride(true);
                          setSelectedStatuses((prev) =>
                            isChecked ? prev.filter((s) => s !== status) : [...prev, status],
                          );
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        <span className={cn(
                          'flex h-4 w-4 items-center justify-center rounded border',
                          isChecked
                            ? 'border-brand-600 bg-brand-600 text-white'
                            : 'border-gray-300 dark:border-gray-600',
                        )}>
                          {isChecked && <span className="text-xs">&#10003;</span>}
                        </span>
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] ?? '#9CA3AF' }} />
                        {t(`orders.statuses.${status}`, status)}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          {/* Compare toggle */}
          <div className="relative">
            <button
              onClick={() => setShowCompareDropdown(!showCompareDropdown)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {compare === 'previousPeriod' ? t('analytics.previousPeriod') : t('analytics.previousYear')}
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            {showCompareDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowCompareDropdown(false)} />
                <div className="absolute right-0 z-20 mt-1 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {([['previousPeriod', t('analytics.previousPeriod')], ['previousYear', t('analytics.previousYear')]] as const).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => { setCompare(val); setShowCompareDropdown(false); }}
                      className={cn(
                        'block w-full px-4 py-2 text-left text-sm transition-colors',
                        compare === val
                          ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
                          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700',
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'bg-white shadow-sm dark:bg-gray-700 dark:text-gray-100'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
              )}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab report={revenueReport} alerts={alertsReport} t={t} dateRange={dateRange} taxIncl={taxIncl} compareLabel={formatCompareLabel(compare, dateRange)} currency={currency} />}
      {activeTab === 'products' && <ProductsTab report={productsReport} categories={categoriesReport} intel={productIntelReport} t={t} navigate={navigate} currency={currency} compareLabel={formatCompareLabel(compare, dateRange)} />}
      {activeTab === 'orders' && <OrdersTab report={ordersReport} intel={orderIntelReport} breakdown={salesBreakdownReport} abandoned={abandonedCartsReport} t={t} taxIncl={taxIncl} compareLabel={formatCompareLabel(compare, dateRange)} />}
      {activeTab === 'attribution' && <AttributionTab report={attributionReport} t={t} compareLabel={formatCompareLabel(compare, dateRange)} />}
      {activeTab === 'customers' && <CustomersTab report={customersReport} intel={customerIntelReport} t={t} taxIncl={taxIncl} compareLabel={formatCompareLabel(compare, dateRange)} />}
      {activeTab === 'stock' && <StockTab report={stockReport} t={t} navigate={navigate} />}
      {activeTab === 'coupons' && <CouponsTab report={couponsReport} revenue={couponsRevenue} t={t} compareLabel={formatCompareLabel(compare, dateRange)} />}
      {activeTab === 'taxes' && <TaxesTab report={taxesReport} t={t} compareLabel={formatCompareLabel(compare, dateRange)} />}
      {activeTab === 'compare' && <CompareTab siteId={activeSiteId} settings={settings} t={t} taxIncl={taxIncl} currency={currency} statusIs={statusIs} />}
      {activeTab === 'settings' && <SettingsTab siteId={activeSiteId} settings={settings} t={t} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 1: OVERVIEW
// ══════════════════════════════════════════════════════════════

function OverviewTab({ report, alerts, t, dateRange, taxIncl, compareLabel, currency }: { report: ReturnType<typeof useRevenueReport>; alerts: ReturnType<typeof useAlerts>; t: ReturnType<typeof useTranslation>['t']; dateRange: { interval: string }; taxIncl?: boolean; compareLabel: string; currency: string }) {
  const { data, isLoading, isError, refetch } = report;
  if (isError) return <ErrorState onRetry={() => refetch()} />;

  const totals = data?.totals;
  const changes = data?.comparison?.changes;
  // WC Analytics API returns gross_sales & net_revenue ALWAYS EXCLUDING TAX.
  // For tax-inclusive stores, WC Admin adds taxes to gross_sales for display.
  const grossDisplay = totals ? (taxIncl ? totals.grossSales + totals.taxesTotal + (totals.shippingTotal ?? 0) : totals.grossSales) : 0;
  const cl = (key: string) => changes?.[key as keyof typeof changes] ? compareLabel : undefined;
  const alertList = alerts.data?.alerts ?? [];

  return (
    <div className="space-y-6">
      {/* Marketing alerts */}
      {alertList.length > 0 && (
        <div className="space-y-2">
          {alertList.map((a, i) => {
            const cls = a.severity === 'critical'
              ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
              : a.severity === 'warning'
              ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300'
              : 'bg-brand-50 border-brand-200 text-brand-800 dark:bg-brand-900/20 dark:border-brand-800 dark:text-brand-300';
            const dot = a.severity === 'critical' ? 'bg-red-500' : a.severity === 'warning' ? 'bg-amber-500' : 'bg-brand-500';
            const alertKey: Record<string, string> = { aov_drop: 'analytics.alertAovDrop', revenue_drop: 'analytics.alertRevenueDrop', high_refunds: 'analytics.alertHighRefunds', low_attribution: 'analytics.alertLowAttribution' };
            const ak = alertKey[a.type];
            const text = ak ? t(ak, { value: a.value, defaultValue: a.message }) : a.message;
            return (
              <div key={a.type + i} className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${cls}`}>
                <span className={`h-2 w-2 rounded-full ${dot}`} />
                <span>{text}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Stat Cards - 2 rows of 5 */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <MetricCard icon={CurrencyEuroIcon} tone="#16a34a" label={t('analytics.grossSales')} value={totals ? formatCurrency(grossDisplay) : '-'} trend={changes?.grossSales} detail={cl('grossSales')} subtitle={taxIncl ? t('analytics.inclVat') : undefined} secondaryValue={totals && taxIncl ? `${t('analytics.exclVat')} ${formatCurrency(totals.grossSales)}` : undefined} loading={isLoading} />
        <MetricCard icon={ChartBarIcon} tone="#956550" label={t('analytics.netRevenue')} value={totals ? formatCurrency(totals.netRevenue) : '-'} trend={changes?.netRevenue} detail={cl('netRevenue')} subtitle={taxIncl ? t('analytics.exclVat') : undefined} loading={isLoading} />
        <MetricCard icon={ShoppingBagIcon} tone="#af7964" label={t('analytics.ordersCount')} value={totals ? formatNumber(totals.ordersCount) : '0'} trend={changes?.ordersCount} detail={cl('ordersCount')} loading={isLoading} />
        <MetricCard icon={CubeIcon} tone="#d97a4a" label={t('analytics.itemsSold')} value={totals ? formatNumber(totals.itemsSold) : '0'} trend={changes?.itemsSold} detail={cl('itemsSold')} loading={isLoading} />
        <MetricCard icon={ReceiptPercentIcon} tone="#7c6ad6" label={t('analytics.avgOrderValue')} value={totals ? formatCurrency(totals.avgOrderValue) : '-'} trend={changes?.avgOrderValue} detail={cl('avgOrderValue')} subtitle={taxIncl ? t('analytics.exclVat') : undefined} loading={isLoading} />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <MetricCard label={t('analytics.avgItemsPerOrder')} value={totals ? totals.avgItemsPerOrder.toFixed(1) : '0'} trend={changes?.avgItemsPerOrder} detail={cl('avgItemsPerOrder')} loading={isLoading} />
        <MetricCard label={t('analytics.couponsUsed')} value={totals ? formatNumber(totals.couponsCount) : '0'} trend={changes?.couponsCount} detail={cl('couponsCount')} loading={isLoading} />
        <MetricCard label={t('analytics.shippingTotal')} value={totals ? formatCurrency(totals.shippingTotal) : '-'} trend={changes?.shippingTotal} detail={cl('shippingTotal')} loading={isLoading} />
        <MetricCard label={t('analytics.taxesTotal')} value={totals ? formatCurrency(totals.taxesTotal) : '-'} trend={changes?.taxesTotal} detail={cl('taxesTotal')} loading={isLoading} />
        <MetricCard label={t('analytics.refundsTotal')} value={totals ? formatCurrency(totals.refundsTotal) : '-'} trend={changes?.refundsTotal} detail={cl('refundsTotal')} loading={isLoading} />
      </div>

      {/* Revenue Chart */}
      <Card title={t('analytics.netRevenue')}>
        {isLoading ? (
          <SkeletonChart />
        ) : data && data.intervals.length > 0 ? (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.intervals}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#af7964" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#af7964" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d: string) => formatDate(d, dateRange.interval === 'month' ? 'MMM' : 'dd/MM')}
                  tick={{ fill: '#9CA3AF', fontSize: 12 }}
                />
                <YAxis tickFormatter={(v: number) => `${currency}${v}`} tick={{ fill: '#9CA3AF', fontSize: 12 }} width={70} />
                <Tooltip
                  formatter={(v, name) => [formatCurrency(Number(v)), name === 'netRevenue' ? t('analytics.netRevenue') : name === 'grossSales' ? t('analytics.grossSales') : String(name)]}
                  labelFormatter={(l) => formatDate(String(l), 'dd MMM yyyy')}
                  contentStyle={{ background: 'var(--tooltip-bg, #fff)', color: 'var(--tooltip-text, #374151)', border: '1px solid var(--tooltip-border, #e5e7eb)', borderRadius: '8px', fontSize: '13px' }}
                />
                <Area type="monotone" dataKey="netRevenue" stroke="#af7964" fill="url(#revenueGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState icon={ChartBarIcon} title={t('analytics.noData')} />
        )}
      </Card>

      {/* Orders Chart */}
      <Card title={t('analytics.ordersCount')}>
        {isLoading ? (
          <SkeletonChart />
        ) : data && data.intervals.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.intervals}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d: string) => formatDate(d, dateRange.interval === 'month' ? 'MMM' : 'dd/MM')}
                  tick={{ fill: '#9CA3AF', fontSize: 12 }}
                />
                <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} width={40} />
                <Tooltip
                  formatter={(v) => [formatNumber(Number(v)), t('analytics.ordersCount')]}
                  labelFormatter={(l) => formatDate(String(l), 'dd MMM yyyy')}
                  contentStyle={{ background: 'var(--tooltip-bg, #fff)', color: 'var(--tooltip-text, #374151)', border: '1px solid var(--tooltip-border, #e5e7eb)', borderRadius: '8px', fontSize: '13px' }}
                />
                <Bar dataKey="ordersCount" fill="#af7964" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState icon={ShoppingBagIcon} title={t('analytics.noData')} />
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 2: PRODUCTS
// ══════════════════════════════════════════════════════════════

function ProductsTab({ report, categories, intel, t, navigate, currency, compareLabel }: {
  report: ReturnType<typeof useProductsReport>;
  categories: ReturnType<typeof useCategoriesReport>;
  intel: ReturnType<typeof useProductIntelligenceReport>;
  t: ReturnType<typeof useTranslation>['t'];
  navigate: ReturnType<typeof useNavigate>;
  currency: string;
  compareLabel: string;
}) {
  const { data, isLoading, isError, refetch } = report;
  const { data: catData, isLoading: catLoading } = categories;
  const pi = intel.data;
  const variations = pi?.topVariations ?? [];
  const fbt = pi?.frequentlyBoughtTogether ?? [];
  const ptotals = data?.totals;
  const pchanges = data?.comparison?.changes;
  const pcl = (key: string) => pchanges?.[key as keyof typeof pchanges] ? compareLabel : undefined;
  const maxProdRev = Math.max(0, ...(data?.products ?? []).map((p) => p.netRevenue));
  const maxCatRev = Math.max(0, ...(catData?.categories ?? []).map((c) => c.netRevenue));

  if (isError) return <ErrorState onRetry={() => refetch()} />;

  return (
    <div className="space-y-6">
      {/* Aggregate KPIs with comparison */}
      {ptotals && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <MetricCard label={t('analytics.netSales')} value={formatCurrency(ptotals.netRevenue)} trend={pchanges?.netRevenue} detail={pcl('netRevenue')} loading={isLoading} />
          <MetricCard label={t('analytics.itemsSold')} value={formatNumber(ptotals.itemsSold)} trend={pchanges?.itemsSold} detail={pcl('itemsSold')} loading={isLoading} />
          <MetricCard label={t('analytics.ordersCount')} value={formatNumber(ptotals.ordersCount)} trend={pchanges?.ordersCount} detail={pcl('ordersCount')} loading={isLoading} />
          <MetricCard label={t('analytics.productsSold', 'Products Sold')} value={formatNumber(ptotals.productsCount)} trend={pchanges?.productsCount} detail={pcl('productsCount')} loading={isLoading} />
        </div>
      )}

      {/* Inventory value (Wave 5b) */}
      {(pi?.stockValue ?? 0) > 0 && (
        <Card title={t('analytics.stockValue', 'Inventory Value')}>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(pi!.stockValue)}</p>
        </Card>
      )}

      {/* Top Products Table */}
      <Card title={t('analytics.topProducts')}>
        {isLoading ? (
          <SkeletonTable rows={10} cols={5} />
        ) : data && data.products.length > 0 ? (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-5 py-2">#</th>
                  <th className="px-5 py-2">{t('analytics.productName')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.unitsSold')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.revenue')} · {t('analytics.exclVat')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.ordersCount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.products.map((item, i) => (
                  <tr
                    key={item.productId}
                    onClick={() => navigate(`/products/${item.productId}`)}
                    className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-5 py-3 text-gray-500">{i + 1}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <ProductImage src={item.imageUrl || null} alt={item.name} size="h-9 w-9" iconSize="h-4 w-4" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{item.name}</p>
                          <ShareBar fraction={maxProdRev > 0 ? item.netRevenue / maxProdRev : 0} tone="#d97a4a" />
                          {item.sku && <p className="mt-1 text-xs text-gray-400">{item.sku}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">
                      <div className="flex flex-col items-end">
                        <span>{formatNumber(item.itemsSold)}</span>
                        <div className="flex items-center gap-1.5">
                          <TrendBadge trend={item.itemsSoldChange} />
                          {item.itemsSoldPrev !== undefined && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatNumber(item.itemsSoldPrev)}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                      <div className="flex flex-col items-end">
                        <span>{formatCurrency(item.netRevenue)}</span>
                        <div className="flex items-center gap-1.5">
                          <TrendBadge trend={item.netRevenueChange} />
                          {item.netRevenuePrev !== undefined && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatCurrency(item.netRevenuePrev)}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">{item.ordersCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={CubeIcon} title={t('analytics.noProductData')} />
        )}
      </Card>

      {/* Category Breakdown */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Category Table */}
        <Card title={t('analytics.categoryBreakdown')}>
          {catLoading ? (
            <SkeletonTable rows={5} cols={4} />
          ) : catData && catData.categories.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-3 py-2">{t('analytics.category')}</th>
                    <th className="px-3 py-2 text-right">{t('analytics.unitsSold')}</th>
                    <th className="px-3 py-2 text-right">{t('analytics.revenue')} · {t('analytics.exclVat')}</th>
                    <th className="px-3 py-2 text-right">{t('analytics.productsCount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {catData.categories.map((cat) => (
                    <tr key={cat.categoryId} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                        <div className="min-w-0">
                          <span>{cat.name}</span>
                          <ShareBar fraction={maxCatRev > 0 ? cat.netRevenue / maxCatRev : 0} tone="#d97a4a" />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">
                        <div className="flex flex-col items-end">
                          <span>{formatNumber(cat.itemsSold)}</span>
                          <div className="flex items-center gap-1.5">
                            <TrendBadge trend={cat.itemsSoldChange} />
                            {cat.itemsSoldPrev !== undefined && (
                              <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatNumber(cat.itemsSoldPrev)}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                        <div className="flex flex-col items-end">
                          <span>{formatCurrency(cat.netRevenue)}</span>
                          <div className="flex items-center gap-1.5">
                            <TrendBadge trend={cat.netRevenueChange} />
                            {cat.netRevenuePrev !== undefined && (
                              <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatCurrency(cat.netRevenuePrev)}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-500">{cat.productsCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon={ChartBarIcon} title={t('analytics.noData')} />
          )}
        </Card>

        {/* Category Pie Chart */}
        <Card title={t('analytics.revenueByCategory')}>
          {catLoading ? (
            <SkeletonChart />
          ) : catData && catData.categories.length > 0 ? (
            <div className="flex items-center gap-6">
              <div className="h-52 w-52 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={catData.categories.map((c) => ({ name: c.name, value: c.netRevenue }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                    >
                      {catData.categories.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(Number(v))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {catData.categories.slice(0, 8).map((c, i) => (
                  <div key={c.categoryId} className="flex items-center gap-2 text-sm">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="text-gray-600 dark:text-gray-300">{c.name}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(c.netRevenue)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyState icon={ChartBarIcon} title={t('analytics.noData')} />
          )}
        </Card>
      </div>

      {/* Top Products Bar Chart */}
      {data && data.products.length > 0 && (
        <Card title={`${t('analytics.topProducts')} (${t('analytics.revenue')})`}>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.products.slice(0, 10).map((p) => ({ name: p.name.length > 20 ? p.name.slice(0, 20) + '...' : p.name, revenue: p.netRevenue }))}
                layout="vertical"
                margin={{ left: 10, right: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis type="number" tickFormatter={(v: number) => `${currency}${v}`} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 11 }} width={140} />
                <Tooltip formatter={(v) => [formatCurrency(Number(v)), t('analytics.revenue')]} />
                <Bar dataKey="revenue" fill="#af7964" radius={[0, 4, 4, 0]}>
                  {data.products.slice(0, 10).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Top Variations (Wave 4) */}
      {variations.length > 0 && (
        <Card title={t('analytics.topVariations', 'Top Variations')}>
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-5 py-2">{t('analytics.productName')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.unitsSold')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.revenue')} · {t('analytics.exclVat')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.ordersCount')}</th>
                </tr>
              </thead>
              <tbody>
                {variations.map((v) => (
                  <tr key={v.variationId} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="px-5 py-2">{v.name}</td>
                    <td className="px-5 py-2 text-right">
                      <div className="flex flex-col items-end">
                        <span>{formatNumber(v.itemsSold)}</span>
                        <div className="flex items-center gap-1.5">
                          <TrendBadge trend={v.itemsSoldChange} />
                          {v.itemsSoldPrev !== undefined && <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatNumber(v.itemsSoldPrev)}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-2 text-right">
                      <div className="flex flex-col items-end">
                        <span>{formatCurrency(v.revenue)}</span>
                        <div className="flex items-center gap-1.5">
                          <TrendBadge trend={v.revenueChange} />
                          {v.revenuePrev !== undefined && <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatCurrency(v.revenuePrev)}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-2 text-right">{formatNumber(v.ordersCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Frequently Bought Together (Wave 4) */}
      {fbt.length > 0 && (
        <Card title={t('analytics.frequentlyBoughtTogether', 'Frequently Bought Together')}>
          <div className="space-y-2">
            {fbt.map((p) => (
              <div key={`${p.productAId}-${p.productBId}`} className="flex items-center justify-between border-b border-gray-50 py-2 dark:border-gray-800">
                <div className="min-w-0 flex-1 truncate text-sm">
                  <span className="text-gray-900 dark:text-gray-100">{p.productA}</span>
                  <span className="text-gray-400"> + </span>
                  <span className="text-gray-900 dark:text-gray-100">{p.productB}</span>
                </div>
                <span className="ml-3 font-semibold text-brand-600 dark:text-brand-400">{formatNumber(p.count)}×</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Stock velocity — running out soonest (Wave 5b) */}
      {(pi?.stockVelocity?.length ?? 0) > 0 && (
        <Card title={t('analytics.stockVelocity', 'Running Out Soon')}>
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-5 py-2">{t('analytics.productName')}</th>
                  <th className="px-5 py-2 text-right">{t('products.stock')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.sold')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.daysLeft', 'days')}</th>
                </tr>
              </thead>
              <tbody>
                {pi!.stockVelocity.map((s) => (
                  <tr key={s.productId} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="px-5 py-2 truncate">{s.name}</td>
                    <td className="px-5 py-2 text-right">{formatNumber(s.stock)}</td>
                    <td className="px-5 py-2 text-right">{formatNumber(s.sold)}</td>
                    <td className={`px-5 py-2 text-right font-semibold ${s.daysOfStock !== null && s.daysOfStock < 14 ? 'text-red-600 dark:text-red-400' : ''}`}>{s.daysOfStock !== null ? s.daysOfStock.toFixed(0) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 3: ORDERS
// ══════════════════════════════════════════════════════════════

function AttributionTab({ report, t, compareLabel }: { report: ReturnType<typeof useAttributionReport>; t: ReturnType<typeof useTranslation>['t']; compareLabel: string }) {
  const { data: d, isLoading, isError, refetch } = report;
  if (isError) return <ErrorState onRetry={() => refetch()} />;
  if (!isLoading && d && !d.available) {
    return <EmptyState icon={ArrowTrendingUpIcon} title={t('analytics.attributionUnavailable')} description={t('analytics.attributionNeedsBridge')} />;
  }
  const ch = d?.comparison?.changes;
  const aov = (r: AttributionRow) => (r.count > 0 ? r.revenue / r.count : 0);
  const topSrc = d?.bySource?.[0];
  const topCamp = d?.byCampaign?.[0];
  const mobile = d?.byDevice?.find((x) => /mobile/i.test(x.value));
  const mobilePct = d && d.totalOrders > 0 && mobile ? (mobile.count / d.totalOrders) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard label={t('analytics.attributionRate')} value={d ? `${d.attributionRate.toFixed(1)}%` : '-'} trend={ch?.attributionRate} detail={ch?.attributionRate ? compareLabel : undefined} subtitle={d ? `${formatNumber(d.attributedOrders)}/${formatNumber(d.totalOrders)}` : undefined} loading={isLoading} />
        <MetricCard label={t('analytics.topSource')} value={topSrc ? topSrc.value : '-'} secondaryValue={topSrc ? formatCurrency(topSrc.revenue) : undefined} loading={isLoading} />
        <MetricCard label={t('analytics.topCampaign')} value={topCamp ? (topCamp.campaign || topCamp.value) : '-'} secondaryValue={topCamp ? formatCurrency(topCamp.revenue) : undefined} loading={isLoading} />
        <MetricCard label={t('analytics.mobileShare')} value={d ? `${mobilePct.toFixed(0)}%` : '-'} loading={isLoading} />
      </div>

      <Card title={t('analytics.sourceConversion')}>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="px-3 py-2">{t('analytics.source')}</th>
                  <th className="px-3 py-2 text-right">{t('analytics.orders')}</th>
                  <th className="px-3 py-2 text-right">{t('analytics.revenue')}</th>
                  <th className="px-3 py-2 text-right">{t('analytics.avgOrderValue')}</th>
                  <th className="px-3 py-2 text-right">%</th>
                </tr>
              </thead>
              <tbody>
                {(d?.bySource || []).map((r) => {
                  const share = d && d.totalOrders ? (r.count / d.totalOrders) * 100 : 0;
                  return (
                    <tr key={r.value} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{r.value}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-col items-end">
                          <span>{formatNumber(r.count)}</span>
                          <div className="flex items-center gap-1.5">
                            <TrendBadge trend={r.countChange} />
                            {r.countPrev !== undefined && <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatNumber(r.countPrev)}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-col items-end">
                          <span>{formatCurrency(r.revenue)}</span>
                          <div className="flex items-center gap-1.5">
                            <TrendBadge trend={r.revenueChange} />
                            {r.revenuePrev !== undefined && <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatCurrency(r.revenuePrev)}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">{formatCurrency(aov(r))}</td>
                      <td className="px-3 py-2 text-right">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title={t('analytics.campaigns')}>
          {(d?.byCampaign || []).slice(0, 10).map((r) => (
            <div key={r.value} className="flex items-center justify-between border-b border-gray-100 py-1.5 text-sm dark:border-gray-700">
              <span className="truncate text-gray-700 dark:text-gray-300">{r.campaign || r.value}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(r.revenue)}</span>
            </div>
          ))}
          {!isLoading && !d?.byCampaign?.length && <p className="py-4 text-center text-sm text-gray-400">{t('analytics.noData')}</p>}
        </Card>
        <Card title={t('analytics.devices')}>
          {(d?.byDevice || []).map((r) => (
            <div key={r.value} className="flex items-center justify-between border-b border-gray-100 py-1.5 text-sm dark:border-gray-700">
              <span className="capitalize text-gray-700 dark:text-gray-300">{r.value}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(r.count)} · {formatCurrency(r.revenue)}</span>
            </div>
          ))}
          {!isLoading && !d?.byDevice?.length && <p className="py-4 text-center text-sm text-gray-400">{t('analytics.noData')}</p>}
        </Card>

        {(d?.byBrowser?.length ?? 0) > 0 && (
          <Card title={t('analytics.browsers', 'Browsers')}>
            {d!.byBrowser.map((r) => (
              <div key={r.value} className="flex items-center justify-between border-b border-gray-100 py-1.5 text-sm dark:border-gray-700">
                <span className="text-gray-700 dark:text-gray-300">{r.value}</span>
                <span className="font-medium">{formatNumber(r.count)} · {formatCurrency(r.revenue)}</span>
              </div>
            ))}
          </Card>
        )}

        {(d?.customersBySource?.length ?? 0) > 0 && (
          <Card title={t('analytics.customersBySource', 'Customers by Source')}>
            {d!.customersBySource.map((r) => (
              <div key={r.value} className="flex items-center justify-between border-b border-gray-100 py-1.5 text-sm dark:border-gray-700">
                <span className="min-w-0 flex-1 truncate text-gray-700 dark:text-gray-300">{r.value}</span>
                <span className="ml-3 font-medium">{formatNumber(r.count)}</span>
              </div>
            ))}
          </Card>
        )}

        {(d?.byLanding?.length ?? 0) > 0 && (
          <Card title={t('analytics.landingPages', 'Landing Pages')}>
            {d!.byLanding.map((r) => (
              <div key={r.value} className="flex items-center justify-between border-b border-gray-100 py-1.5 text-sm dark:border-gray-700">
                <span className="min-w-0 flex-1 truncate text-gray-700 dark:text-gray-300">{r.value}</span>
                <span className="ml-3 font-medium">{formatNumber(r.count)}</span>
              </div>
            ))}
          </Card>
        )}
      </div>

      {d && d.available && d.attributionRate < 60 && (
        <p className="text-xs text-gray-400 dark:text-gray-500">{t('analytics.attributionCoverageNote', { pct: d.attributionRate.toFixed(0) })}</p>
      )}
    </div>
  );
}

function OrdersTab({ report, intel, breakdown, abandoned, t, taxIncl, compareLabel }: { report: ReturnType<typeof useOrdersReport>; intel: ReturnType<typeof useOrderIntelligenceReport>; breakdown: ReturnType<typeof useSalesBreakdown>; abandoned: ReturnType<typeof useAbandonedCarts>; t: ReturnType<typeof useTranslation>['t']; taxIncl?: boolean; compareLabel: string }) {
  const { data, isLoading, isError, refetch } = report;
  if (isError) return <ErrorState onRetry={() => refetch()} />;

  const ab = abandoned.data;
  const totals = data?.totals;
  const changes = data?.comparison?.changes;
  const cl = (key: string) => changes?.[key as keyof typeof changes] ? compareLabel : undefined;

  const brk = breakdown.data;
  const dowData = (brk?.byDayOfWeek ?? []).map((d) => ({ label: new Date(2023, 0, Number(d.key)).toLocaleDateString(undefined, { weekday: 'short' }), orders: d.orders }));
  const shipping = brk?.byShipping ?? [];

  const intelData = intel.data;
  const profit = intelData?.profit;
  const refunds = intelData?.refunds;
  const intelChanges = intelData?.comparison?.changes;
  const valueDist = intelData?.valueDistribution ?? [];
  const hasProfit = !!profit && profit.grossProfit !== null && profit.margin !== null;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard label={t('analytics.ordersCount')} value={totals ? formatNumber(totals.ordersCount) : '0'} trend={changes?.ordersCount} detail={cl('ordersCount')} loading={isLoading} />
        <MetricCard label={t('analytics.netSales')} value={totals ? formatCurrency(totals.netRevenue) : '-'} trend={changes?.netRevenue} detail={cl('netRevenue')} subtitle={taxIncl ? t('analytics.exclVat') : undefined} loading={isLoading} />
        <MetricCard label={t('analytics.avgItems')} value={totals ? totals.avgItemsPerOrder.toFixed(1) : '0'} trend={changes?.avgItemsPerOrder} detail={cl('avgItemsPerOrder')} loading={isLoading} />
        <MetricCard label={t('analytics.avgOrderValue')} value={totals ? formatCurrency(totals.avgOrderValue) : '-'} trend={changes?.avgOrderValue} detail={cl('avgOrderValue')} subtitle={taxIncl ? t('analytics.exclVat') : undefined} loading={isLoading} />
      </div>

      {/* Orders Over Time */}
      <Card title={t('analytics.ordersByDay')}>
        {isLoading ? (
          <SkeletonChart />
        ) : data && data.intervals.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.intervals}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis dataKey="date" tickFormatter={(d: string) => formatDate(d, 'dd/MM')} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} width={40} />
                <Tooltip
                  formatter={(v, name) => [String(name) === 'ordersCount' ? formatNumber(Number(v)) : formatCurrency(Number(v)), String(name) === 'ordersCount' ? t('analytics.ordersCount') : t('analytics.netRevenue')]}
                  labelFormatter={(l) => formatDate(String(l), 'dd MMM yyyy')}
                  contentStyle={{ background: 'var(--tooltip-bg, #fff)', color: 'var(--tooltip-text, #374151)', border: '1px solid var(--tooltip-border, #e5e7eb)', borderRadius: '8px', fontSize: '13px' }}
                />
                <Bar dataKey="ordersCount" fill="#af7964" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState icon={ShoppingBagIcon} title={t('analytics.noData')} />
        )}
      </Card>

      {/* Wave 2: Profit & Margin (requires product cost data) */}
      {intelData?.available && (
        <Card title={t('analytics.profitMargin', 'Profit & Margin')}>
          {hasProfit ? (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <MetricCard label={t('analytics.grossProfit', 'Gross profit')} value={formatCurrency(profit!.grossProfit!)} trend={intelChanges?.grossProfit} detail={intelChanges?.grossProfit ? compareLabel : undefined} loading={intel.isLoading} />
              <MetricCard label={t('analytics.margin', 'Margin')} value={`${profit!.margin!.toFixed(1)}%`} trend={intelChanges?.margin} detail={intelChanges?.margin ? compareLabel : undefined} loading={intel.isLoading} />
              <MetricCard label={t('analytics.cogs', 'Cost of goods')} value={formatCurrency(profit!.cogs)} trend={intelChanges?.cogs} detail={intelChanges?.cogs ? compareLabel : undefined} loading={intel.isLoading} />
              <MetricCard label={t('analytics.costCoverage', 'Cost coverage')} value={`${(profit!.costCoverage * 100).toFixed(0)}%`} subtitle={t('analytics.costCoverageHint', 'of revenue has cost data')} loading={intel.isLoading} />
            </div>
          ) : (
            <EmptyState icon={ShoppingBagIcon} title={t('analytics.noCostData', 'No product cost data')} description={t('analytics.noCostDataHint', 'Add cost of goods to your products to see profit and margin.')} />
          )}
        </Card>
      )}

      {/* Wave 6: Abandoned Cart Recovery */}
      {ab?.available && ab.capturedCarts > 0 && (
        <Card title={t('analytics.abandonedCarts', 'Abandoned Cart Recovery')}>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <MetricCard label={t('analytics.cartsCaptured', 'Carts Captured')} value={formatNumber(ab.capturedCarts)} loading={abandoned.isLoading} />
            <MetricCard label={t('analytics.recoveryRate', 'Recovery Rate')} value={`${ab.recoveryRate.toFixed(1)}%`} subtitle={`${formatNumber(ab.recoveredCarts)} ${t('analytics.recovered', 'recovered')}`} loading={abandoned.isLoading} />
            <MetricCard label={t('analytics.recoveredValue', 'Recovered Value')} value={formatCurrency(ab.recoveredValue)} loading={abandoned.isLoading} />
            <MetricCard label={t('analytics.lostValue', 'Lost Value')} value={formatCurrency(ab.lostValue)} loading={abandoned.isLoading} />
          </div>
        </Card>
      )}

      {/* Wave 2: Refunds */}
      {refunds && refunds.count > 0 && (
        <Card title={t('analytics.refunds', 'Refunds')}>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <MetricCard label={t('analytics.refundRate', 'Refund rate')} value={`${refunds.rate.toFixed(1)}%`} trend={intelChanges?.refundRate} detail={intelChanges?.refundRate ? compareLabel : undefined} loading={intel.isLoading} />
            <MetricCard label={t('analytics.refundsTotal')} value={formatCurrency(refunds.total)} loading={intel.isLoading} />
            <MetricCard label={t('analytics.refundsCount', 'Refunds')} value={formatNumber(refunds.count)} loading={intel.isLoading} />
            <MetricCard label={t('analytics.avgRefund', 'Avg refund')} value={formatCurrency(refunds.avg)} loading={intel.isLoading} />
          </div>
        </Card>
      )}

      {/* Wave 2: Order value distribution */}
      {valueDist.length > 0 && (
        <Card title={t('analytics.orderValueDistribution', 'Order value distribution')}>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={valueDist}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis dataKey="bucket" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} width={40} />
                <Tooltip
                  formatter={(v, name) => [String(name) === 'revenue' ? formatCurrency(Number(v)) : formatNumber(Number(v)), String(name) === 'revenue' ? t('analytics.revenue') : t('analytics.ordersCount')]}
                  contentStyle={{ background: 'var(--tooltip-bg, #fff)', color: 'var(--tooltip-text, #374151)', border: '1px solid var(--tooltip-border, #e5e7eb)', borderRadius: '8px', fontSize: '13px' }}
                />
                <Bar dataKey="count" fill="#af7964" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Sales by day of week (Wave 5) */}
      {dowData.length > 0 && (
        <Card title={t('analytics.salesByDay', 'Sales by Day')}>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dowData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis dataKey="label" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                <YAxis tick={{ fill: '#9CA3AF', fontSize: 12 }} width={40} />
                <Tooltip formatter={(v) => [formatNumber(Number(v)), t('analytics.ordersCount')]} contentStyle={{ background: 'var(--tooltip-bg, #fff)', color: 'var(--tooltip-text, #374151)', border: '1px solid var(--tooltip-border, #e5e7eb)', borderRadius: '8px', fontSize: '13px' }} />
                <Bar dataKey="orders" fill="#af7964" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Shipping methods (Wave 5) */}
      {shipping.length > 0 && (
        <Card title={t('analytics.shippingMethods', 'Shipping Methods')}>
          <div className="space-y-2">
            {shipping.map((s) => (
              <div key={s.key} className="flex items-center justify-between border-b border-gray-50 py-2 text-sm dark:border-gray-800">
                <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-gray-100">{s.key}</span>
                <div className="ml-3 flex items-center gap-2">
                  <span className="font-semibold">{formatNumber(s.orders)}</span>
                  <TrendBadge trend={s.ordersChange} />
                  {s.ordersPrev !== undefined && <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatNumber(s.ordersPrev)}</span>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Top cities (Wave 5b) */}
      {(brk?.byCity?.length ?? 0) > 0 && (
        <Card title={t('analytics.topCities', 'Top Cities')}>
          <div className="space-y-2">
            {brk!.byCity.map((c) => (
              <div key={c.key} className="flex items-center justify-between border-b border-gray-50 py-2 text-sm dark:border-gray-800">
                <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-gray-100">{c.key}</span>
                <div className="ml-3 flex items-center gap-2">
                  <span className="font-semibold">{formatCurrency(c.revenue)}</span>
                  <TrendBadge trend={c.revenueChange} />
                  {c.revenuePrev !== undefined && <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatCurrency(c.revenuePrev)}</span>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 4: CUSTOMERS
// ══════════════════════════════════════════════════════════════

function CustomersTab({ report, intel, t, taxIncl, compareLabel }: { report: ReturnType<typeof useCustomersReport>; intel: ReturnType<typeof useCustomerIntelligenceReport>; t: ReturnType<typeof useTranslation>['t']; taxIncl?: boolean; compareLabel: string }) {
  const { data, isLoading, isError, refetch } = report;
  if (isError) return <ErrorState onRetry={() => refetch()} />;

  const totals = data?.totals;
  const ci = intel.data;
  const cic = ci?.comparison?.changes;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard label={t('analytics.totalCustomersCount')} value={totals ? formatNumber(totals.customersCount) : '0'} loading={isLoading} />
        <MetricCard label={t('analytics.avgOrders')} value={totals ? totals.avgOrdersCount.toFixed(1) : '0'} loading={isLoading} />
        <MetricCard label={t('analytics.avgSpend')} value={totals ? formatCurrency(totals.avgTotalSpend) : '-'} subtitle={taxIncl ? t('analytics.exclVat') : undefined} loading={isLoading} />
        <MetricCard label={t('analytics.avgOrderValue')} value={totals ? formatCurrency(totals.avgAvgOrderValue) : '-'} subtitle={taxIncl ? t('analytics.exclVat') : undefined} loading={isLoading} />
      </div>

      {/* Customer Intelligence (Wave 3) */}
      {ci?.available && ci.activeCustomers > 0 && (
        <Card title={t('analytics.customerIntel', 'Customer Intelligence')}>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <MetricCard label={t('analytics.newCustomers', 'New Customers')} value={formatNumber(ci.newCustomers)} trend={cic?.newCustomers} detail={cic?.newCustomers ? compareLabel : undefined} loading={intel.isLoading} />
            <MetricCard label={t('analytics.returningCustomers', 'Returning')} value={formatNumber(ci.returningCustomers)} trend={cic?.returningCustomers} detail={cic?.returningCustomers ? compareLabel : undefined} loading={intel.isLoading} />
            <MetricCard label={t('analytics.repeatRate', 'Repeat Rate')} value={`${ci.repeatRate.toFixed(1)}%`} trend={cic?.repeatRate} detail={cic?.repeatRate ? compareLabel : undefined} loading={intel.isLoading} />
            <MetricCard label={t('analytics.avgLifetimeValue', 'Avg LTV')} value={formatCurrency(ci.avgLifetimeValue)} trend={cic?.avgLifetimeValue} detail={cic?.avgLifetimeValue ? compareLabel : undefined} subtitle={`${ci.avgLifetimeOrders.toFixed(1)} ${t('analytics.ordersPerCustomer', 'orders/customer')}`} loading={intel.isLoading} />
          </div>
        </Card>
      )}

      {/* RFM Segments (Wave 3b) */}
      {ci?.available && ci.activeCustomers > 0 && (
        <Card title={t('analytics.rfmSegments', 'Customer Segments (RFM)')}>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <MetricCard label={t('analytics.platinum', 'Platinum')} value={formatNumber(ci.rfm.platinum)} loading={intel.isLoading} />
            <MetricCard label={t('analytics.gold', 'Gold')} value={formatNumber(ci.rfm.gold)} loading={intel.isLoading} />
            <MetricCard label={t('analytics.silver', 'Silver')} value={formatNumber(ci.rfm.silver)} loading={intel.isLoading} />
            <MetricCard label={t('analytics.bronze', 'Bronze')} value={formatNumber(ci.rfm.bronze)} loading={intel.isLoading} />
          </div>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            {t('analytics.avgDaysBetween', 'Avg days between orders')}: {ci.avgDaysBetween.toFixed(0)} · {t('analytics.avgRecency', 'Avg recency')}: {ci.avgRecencyDays.toFixed(0)}d
          </p>
        </Card>
      )}

      {/* Cohort retention (Wave 3b) */}
      {(ci?.cohorts?.length ?? 0) > 0 && (
        <Card title={t('analytics.cohorts', 'Cohort Retention')}>
          <div className="space-y-2">
            {ci!.cohorts.map((co) => (
              <div key={co.month} className="flex items-center gap-3 text-sm">
                <span className="w-16 text-gray-900 dark:text-gray-100">{co.month}</span>
                <span className="w-24 text-gray-500">{formatNumber(co.size)} {t('nav.customers').toLowerCase()}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                  <div className="h-2 rounded-full bg-green-500" style={{ width: `${Math.max(co.repeatRate, 2)}%` }} />
                </div>
                <span className="w-12 text-right font-semibold">{co.repeatRate.toFixed(0)}%</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{t('analytics.cohortsHint', '% of each acquisition month that ordered again')}</p>
        </Card>
      )}

      {/* Top Customers by IN-PERIOD spend (Wave 3 enhancement) */}
      {(ci?.topCustomers?.length ?? 0) > 0 && (
        <Card title={t('analytics.topCustomersPeriod', 'Top Customers (this period)')}>
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-5 py-2">{t('common.email')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.periodSpend', 'Period Spend')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.lifetimeShort', 'lifetime')}</th>
                </tr>
              </thead>
              <tbody>
                {ci!.topCustomers.map((c, i) => (
                  <tr key={c.email + i} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="px-5 py-2 truncate">{c.email || '—'}</td>
                    <td className="px-5 py-2 text-right font-semibold">{formatCurrency(c.periodSpend)}</td>
                    <td className="px-5 py-2 text-right text-gray-500">{formatCurrency(c.lifetimeValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Top Customers (lifetime) */}
      <Card title={t('analytics.topCustomersTitle')}>
        {isLoading ? (
          <SkeletonTable rows={10} cols={5} />
        ) : data && data.customers.length > 0 ? (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-5 py-2">#</th>
                  <th className="px-5 py-2">{t('orders.customer')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.ordersCount')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.totalSpent')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.avgOrderValue')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.lastActive')}</th>
                </tr>
              </thead>
              <tbody>
                {data.customers.map((c, i) => (
                  <tr key={c.id} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="px-5 py-3 text-gray-500">{i + 1}</td>
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{c.name}</p>
                      <p className="text-xs text-gray-400">{c.email}</p>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">{c.ordersCount}</td>
                    <td className="px-5 py-3 text-right font-medium text-gray-900 dark:text-gray-100">{formatCurrency(c.totalSpend)}</td>
                    <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">{formatCurrency(c.avgOrderValue)}</td>
                    <td className="px-5 py-3 text-right text-gray-500">{c.dateLastActive ? formatRelativeTime(c.dateLastActive) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={UsersIcon} title={t('analytics.noCustomers')} />
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 5: STOCK
// ══════════════════════════════════════════════════════════════

function StockTab({ report, t, navigate }: { report: ReturnType<typeof useStockReport>; t: ReturnType<typeof useTranslation>['t']; navigate: ReturnType<typeof useNavigate> }) {
  const { data, isLoading, isError, refetch } = report;
  if (isError) return <ErrorState onRetry={() => refetch()} />;

  const totals = data?.totals;
  const stockStatusData = totals
    ? [
        { name: t('analytics.inStock'), value: totals.instock, color: STOCK_COLORS.instock },
        { name: t('analytics.lowStockLabel'), value: totals.lowstock, color: STOCK_COLORS.lowstock },
        { name: t('analytics.outOfStockLabel'), value: totals.outofstock, color: STOCK_COLORS.outofstock },
        { name: t('analytics.onBackorder'), value: totals.onbackorder, color: STOCK_COLORS.onbackorder },
      ].filter((s) => s.value > 0)
    : [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard title={t('analytics.inStock')} value={totals ? formatNumber(totals.instock) : '0'} icon={CubeIcon} isLoading={isLoading} />
        <StatCard title={t('analytics.lowStockLabel')} value={totals ? formatNumber(totals.lowstock) : '0'} icon={ExclamationTriangleIcon} isLoading={isLoading} />
        <StatCard title={t('analytics.outOfStockLabel')} value={totals ? formatNumber(totals.outofstock) : '0'} icon={ExclamationTriangleIcon} isLoading={isLoading} />
        <StatCard title={t('analytics.onBackorder')} value={totals ? formatNumber(totals.onbackorder) : '0'} icon={CubeIcon} isLoading={isLoading} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Pie Chart */}
        <Card title={t('analytics.stockSummary')}>
          {isLoading ? (
            <SkeletonChart />
          ) : stockStatusData.length > 0 ? (
            <div className="flex items-center gap-6">
              <div className="h-48 w-48 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={stockStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70}>
                      {stockStatusData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {stockStatusData.map((s) => (
                  <div key={s.name} className="flex items-center gap-2 text-sm">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="text-gray-600 dark:text-gray-300">{s.name}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyState icon={CubeIcon} title={t('analytics.noStockData')} />
          )}
        </Card>

        {/* Low Stock Products Table */}
        <Card title={t('analytics.lowStockProducts')}>
          {isLoading ? (
            <SkeletonTable rows={5} cols={4} />
          ) : data && data.products.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-3 py-2">{t('analytics.productName')}</th>
                    <th className="px-3 py-2">{t('products.sku')}</th>
                    <th className="px-3 py-2 text-right">{t('analytics.quantity')}</th>
                    <th className="px-3 py-2">{t('analytics.stockStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.products.map((p) => (
                    <tr
                      key={p.productId}
                      onClick={() => navigate(`/products/${p.productId}`)}
                      className="cursor-pointer border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <ProductImage src={p.imageUrl || null} alt={p.name || ''} size="h-8 w-8" iconSize="h-4 w-4" />
                          <span className="font-medium text-gray-900 dark:text-gray-100">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-500">{p.sku || '-'}</td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{p.stockQuantity ?? '-'}</td>
                      <td className="px-3 py-2">
                        <span className={cn(
                          'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                          p.stockStatus === 'outofstock' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : p.stockStatus === 'onbackorder' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                        )}>
                          {p.stockStatus === 'outofstock' ? t('analytics.outOfStockLabel')
                            : p.stockStatus === 'onbackorder' ? t('analytics.onBackorder')
                            : t('analytics.lowStockLabel')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon={CubeIcon} title={t('analytics.noStockData')} />
          )}
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 6: COUPONS
// ══════════════════════════════════════════════════════════════

function CouponsTab({ report, revenue, t, compareLabel }: { report: ReturnType<typeof useCouponsReport>; revenue: ReturnType<typeof useRevenueReport>; t: ReturnType<typeof useTranslation>['t']; compareLabel: string }) {
  const { data, isLoading, isError, refetch } = report;
  if (isError) return <ErrorState onRetry={() => refetch()} />;

  const totals = data?.totals;
  const changes = data?.comparison?.changes;
  const cl = (key: string) => changes?.[key as keyof typeof changes] ? compareLabel : undefined;
  const gross = revenue.data?.totals?.grossSales ?? 0;
  const discountPct = totals && gross > 0 ? (totals.amount / gross) * 100 : null;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard label={t('analytics.ordersWithCoupons')} value={totals ? formatNumber(totals.ordersCount) : '0'} trend={changes?.ordersCount} detail={cl('ordersCount')} loading={isLoading} />
        <MetricCard label={t('analytics.totalDiscount')} value={totals ? formatCurrency(totals.amount) : '-'} trend={changes?.amount} detail={cl('amount')} loading={isLoading} />
        <MetricCard label={t('analytics.couponsUsed')} value={totals ? formatNumber(totals.couponsCount) : '0'} trend={changes?.couponsCount} detail={cl('couponsCount')} loading={isLoading} />
        <MetricCard label={t('analytics.discountOfGross', '% of gross')} value={discountPct !== null ? `${discountPct.toFixed(1)}%` : '-'} loading={isLoading} />
      </div>

      {/* Top Coupons Table */}
      <Card title={t('analytics.topCoupons')}>
        {isLoading ? (
          <SkeletonTable rows={5} cols={3} />
        ) : data && data.coupons.length > 0 ? (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-5 py-2">{t('analytics.couponCode')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.ordersCount')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.discount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.coupons.map((c) => (
                  <tr key={c.couponId} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="px-5 py-3">
                      <span className="rounded-md bg-gray-100 px-2 py-1 font-mono text-sm font-medium text-gray-900 dark:bg-gray-700 dark:text-gray-100">
                        {c.code}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">
                      <div className="flex flex-col items-end">
                        <span>{c.ordersCount}</span>
                        <div className="flex items-center gap-1.5">
                          <TrendBadge trend={c.ordersCountChange} />
                          {c.ordersCountPrev !== undefined && <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatNumber(c.ordersCountPrev)}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                      <div className="flex flex-col items-end">
                        <span>{formatCurrency(c.amount)}</span>
                        <div className="flex items-center gap-1.5">
                          <TrendBadge trend={c.amountChange} />
                          {c.amountPrev !== undefined && <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatCurrency(c.amountPrev)}</span>}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={TicketIcon} title={t('analytics.noCouponData')} />
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 7: TAXES
// ══════════════════════════════════════════════════════════════

function TaxesTab({ report, t, compareLabel }: { report: ReturnType<typeof useTaxesReport>; t: ReturnType<typeof useTranslation>['t']; compareLabel: string }) {
  const { data, isLoading, isError, refetch } = report;
  if (isError) return <ErrorState onRetry={() => refetch()} />;

  const totals = data?.totals;
  const changes = data?.comparison?.changes;
  const cl = (key: string) => changes?.[key as keyof typeof changes] ? compareLabel : undefined;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard label={t('analytics.totalTax')} value={totals ? formatCurrency(totals.totalTax) : '-'} trend={changes?.totalTax} detail={cl('totalTax')} loading={isLoading} />
        <MetricCard label={t('analytics.orderTax')} value={totals ? formatCurrency(totals.orderTax) : '-'} trend={changes?.orderTax} detail={cl('orderTax')} loading={isLoading} />
        <MetricCard label={t('analytics.shippingTax')} value={totals ? formatCurrency(totals.shippingTax) : '-'} trend={changes?.shippingTax} detail={cl('shippingTax')} loading={isLoading} />
      </div>

      {/* Tax Rates Table */}
      <Card title={t('analytics.taxRates')}>
        {isLoading ? (
          <SkeletonTable rows={5} cols={5} />
        ) : data && data.taxRates.length > 0 ? (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-5 py-2">{t('common.name')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.taxRate')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.orderTax')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.shippingTax')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.totalTax')}</th>
                </tr>
              </thead>
              <tbody>
                {data.taxRates.map((rate) => (
                  <tr key={rate.taxRateId} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">{rate.name || '-'}</td>
                    <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">{rate.rate}%</td>
                    <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">{formatCurrency(rate.orderTax)}</td>
                    <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">{formatCurrency(rate.shippingTax)}</td>
                    <td className="px-5 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                      <div className="flex flex-col items-end">
                        <span>{formatCurrency(rate.totalTax)}</span>
                        <div className="flex items-center gap-1.5">
                          <TrendBadge trend={rate.totalTaxChange} />
                          {rate.totalTaxPrev !== undefined && <span className="text-[10px] text-gray-400 dark:text-gray-500">vs {formatCurrency(rate.totalTaxPrev)}</span>}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={ReceiptPercentIcon} title={t('analytics.noTaxData')} />
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 8: COMPARE
// ══════════════════════════════════════════════════════════════

const COMPARE_METRICS: Array<{ key: keyof import('@wooconnect/shared').RevenueReportTotals; isCurrency: boolean }> = [
  { key: 'grossSales', isCurrency: true },
  { key: 'netRevenue', isCurrency: true },
  { key: 'ordersCount', isCurrency: false },
  { key: 'itemsSold', isCurrency: false },
  { key: 'avgOrderValue', isCurrency: true },
  { key: 'taxesTotal', isCurrency: true },
  { key: 'shippingTotal', isCurrency: true },
  { key: 'couponsTotal', isCurrency: true },
  { key: 'refundsTotal', isCurrency: true },
];

const COMPARE_DATE_PRESETS: Array<{ key: AnalyticsDateRangePreset; labelKey: string }> = [
  { key: 'today', labelKey: 'analytics.today' },
  { key: 'yesterday', labelKey: 'analytics.yesterday' },
  { key: 'last7days', labelKey: 'analytics.last7days' },
  { key: 'last14days', labelKey: 'analytics.last14days' },
  { key: 'last30days', labelKey: 'analytics.last30days' },
  { key: 'last90days', labelKey: 'analytics.last90days' },
  { key: 'last365days', labelKey: 'analytics.last365days' },
  { key: 'thisWeek', labelKey: 'analytics.thisWeek' },
  { key: 'lastWeek', labelKey: 'analytics.lastWeek' },
  { key: 'thisMonth', labelKey: 'analytics.thisMonth' },
  { key: 'lastMonth', labelKey: 'analytics.lastMonth' },
  { key: 'thisQuarter', labelKey: 'analytics.thisQuarter' },
  { key: 'thisYear', labelKey: 'analytics.thisYear' },
  { key: 'custom', labelKey: 'analytics.customRange' },
];

type PeriodBMode = 'previousPeriod' | 'previousYear' | 'custom';

function computeComparePeriodB(
  mode: PeriodBMode,
  periodA: { after: string; before: string; interval: string },
  customFrom: string,
  customTo: string,
): { after: string; before: string; interval: string } {
  if (mode === 'custom' && customFrom && customTo) {
    const days = Math.round((new Date(customTo).getTime() - new Date(customFrom).getTime()) / 86400000);
    const interval = days > 180 ? 'month' : days > 30 ? 'week' : 'day';
    return { after: `${customFrom}T00:00:00`, before: `${customTo}T23:59:59`, interval };
  }
  if (mode === 'previousYear') {
    const shiftYear = (dateStr: string) => {
      const d = new Date(dateStr);
      d.setFullYear(d.getFullYear() - 1);
      return d.toISOString().split('T')[0];
    };
    const afterDate = periodA.after.split('T')[0] ?? periodA.after;
    const beforeDate = periodA.before.split('T')[0] ?? periodA.before;
    return { after: `${shiftYear(afterDate)}T00:00:00`, before: `${shiftYear(beforeDate)}T23:59:59`, interval: periodA.interval };
  }
  // previousPeriod: shift backward by same duration
  const afterMs = new Date(periodA.after).getTime();
  const beforeMs = new Date(periodA.before).getTime();
  const durationMs = beforeMs - afterMs;
  const prevAfter = new Date(afterMs - durationMs);
  const prevBefore = new Date(afterMs - 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { after: `${fmt(prevAfter)}T00:00:00`, before: `${fmt(prevBefore)}T23:59:59`, interval: periodA.interval };
}

function CompareTab({ siteId, settings, t, taxIncl, currency, statusIs }: { siteId: string; settings: SiteAnalyticsSettings; t: ReturnType<typeof useTranslation>['t']; taxIncl?: boolean; currency: string; statusIs?: string }) {
  // Period A
  const [periodAPreset, setPeriodAPreset] = useState<AnalyticsDateRangePreset>(settings.defaultPreset);
  const [periodAFrom, setPeriodAFrom] = useState('');
  const [periodATo, setPeriodATo] = useState('');

  // Period B
  const [periodBMode, setPeriodBMode] = useState<PeriodBMode>(
    settings.compareDefaultPreset === 'previousYear' ? 'previousYear' : 'previousPeriod',
  );
  const [periodBFrom, setPeriodBFrom] = useState('');
  const [periodBTo, setPeriodBTo] = useState('');

  const rangeA = useMemo(() => {
    if (periodAPreset === 'custom' && periodAFrom && periodATo) {
      const days = Math.round((new Date(periodATo).getTime() - new Date(periodAFrom).getTime()) / 86400000);
      const interval: 'day' | 'week' | 'month' = days > 180 ? 'month' : days > 30 ? 'week' : 'day';
      return { after: `${periodAFrom}T00:00:00`, before: `${periodATo}T23:59:59`, interval };
    }
    return computeDateRange(periodAPreset, settings.weekStartsOn, settings.timezone || undefined);
  }, [periodAPreset, settings.weekStartsOn, settings.timezone, periodAFrom, periodATo]);

  const rangeB = useMemo(
    () => computeComparePeriodB(periodBMode, rangeA, periodBFrom, periodBTo),
    [periodBMode, rangeA, periodBFrom, periodBTo],
  );

  const reportA = useRevenueReport(siteId, { after: rangeA.after, before: rangeA.before, interval: rangeA.interval, compare: 'previousPeriod', status_is: statusIs });
  const reportB = useRevenueReport(siteId, { after: rangeB.after, before: rangeB.before, interval: rangeB.interval, compare: 'previousPeriod', status_is: statusIs });

  const isLoading = reportA.isLoading || reportB.isLoading;
  const isError = reportA.isError || reportB.isError;

  if (isError) return <ErrorState onRetry={() => { reportA.refetch(); reportB.refetch(); }} />;

  const totalsA = reportA.data?.totals;
  const totalsB = reportB.data?.totals;

  const fmt = (v: number, isCurrency: boolean) => isCurrency ? formatCurrency(v) : formatNumber(v);

  const periodBOptions: Array<{ value: PeriodBMode; label: string }> = [
    { value: 'previousPeriod', label: t('analytics.previousPeriod') },
    { value: 'previousYear', label: t('analytics.previousYear') },
    { value: 'custom', label: t('analytics.customDates') },
  ];

  // Chart data: merge by relative index using the longer array
  const intervalsA = reportA.data?.intervals ?? [];
  const intervalsB = reportB.data?.intervals ?? [];
  const maxLen = Math.max(intervalsA.length, intervalsB.length);
  const chartData = Array.from({ length: maxLen }, (_, i) => ({
    label: formatDate(intervalsA[i]?.date ?? intervalsB[i]?.date, rangeA.interval === 'month' ? 'MMM' : 'dd/MM'),
    periodA: intervalsA[i]?.netRevenue ?? 0,
    periodB: intervalsB[i]?.netRevenue ?? 0,
  }));

  return (
    <div className="space-y-6">
      {/* Period Selectors */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Period A */}
        <Card title={t('analytics.periodA')}>
          <div className="space-y-3">
            <select
              value={periodAPreset}
              onChange={(e) => setPeriodAPreset(e.target.value as AnalyticsDateRangePreset)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              {COMPARE_DATE_PRESETS.map((p) => (
                <option key={p.key} value={p.key}>{t(p.labelKey)}</option>
              ))}
            </select>
            {periodAPreset === 'custom' && (
              <div className="flex items-center gap-2">
                <DatePicker value={periodAFrom} onChange={setPeriodAFrom} label={t('analytics.customFrom')} className="flex-1" compact />
                <DatePicker value={periodATo} onChange={setPeriodATo} min={periodAFrom} label={t('analytics.customTo')} className="flex-1" compact />
              </div>
            )}
          </div>
        </Card>

        {/* Period B */}
        <Card title={t('analytics.periodB')}>
          <div className="space-y-3">
            <select
              value={periodBMode}
              onChange={(e) => setPeriodBMode(e.target.value as PeriodBMode)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              {periodBOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {periodBMode === 'custom' && (
              <div className="flex items-center gap-2">
                <DatePicker value={periodBFrom} onChange={setPeriodBFrom} label={t('analytics.customFrom')} className="flex-1" compact />
                <DatePicker value={periodBTo} onChange={setPeriodBTo} min={periodBFrom} label={t('analytics.customTo')} className="flex-1" compact />
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Comparison Table */}
      <Card title={t('analytics.compare')}>
        {isLoading ? (
          <SkeletonTable rows={9} cols={4} />
        ) : totalsA && totalsB ? (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-5 py-2">{t('analytics.metric')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.periodA')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.periodB')}</th>
                  <th className="px-5 py-2 text-right">{t('analytics.difference')}</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_METRICS.map(({ key, isCurrency }) => {
                  // For taxIncl stores, adjust grossSales to include taxes (matching WC Admin)
                  let valA = totalsA[key] ?? 0;
                  let valB = totalsB[key] ?? 0;
                  if (key === 'grossSales' && taxIncl) {
                    valA += (totalsA.taxesTotal ?? 0) + (totalsA.shippingTotal ?? 0);
                    valB += (totalsB.taxesTotal ?? 0) + (totalsB.shippingTotal ?? 0);
                  }
                  const vatHint = taxIncl && (key === 'grossSales') ? t('analytics.inclVat')
                    : taxIncl && (key === 'netRevenue' || key === 'avgOrderValue') ? t('analytics.exclVat')
                    : '';
                  const pct = valB !== 0 ? ((valA - valB) / Math.abs(valB)) * 100 : valA > 0 ? 100 : 0;
                  const trend: import('@wooconnect/shared').AnalyticsTrend | undefined = valA !== valB
                    ? { value: valA - valB, percentage: Math.abs(pct), trend: pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral' }
                    : undefined;
                  return (
                    <tr key={key} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="px-5 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {t(`analytics.${key}`)}
                        {vatHint && <span className="ml-1 text-xs font-normal text-gray-400">({vatHint})</span>}
                      </td>
                      <td className="px-5 py-3 text-right text-gray-900 dark:text-gray-100">{fmt(valA, isCurrency)}</td>
                      <td className="px-5 py-3 text-right text-gray-500 dark:text-gray-400">{fmt(valB, isCurrency)}</td>
                      <td className="px-5 py-3 text-right">
                        <TrendBadge trend={trend} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={ArrowsRightLeftIcon} title={t('analytics.noData')} />
        )}
      </Card>

      {/* Revenue Comparison Chart */}
      {!isLoading && chartData.length > 0 && (
        <Card title={t('analytics.revenueComparison')}>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="compareGradA" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#af7964" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#af7964" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="compareGradB" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9CA3AF" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#9CA3AF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis dataKey="label" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                <YAxis tickFormatter={(v: number) => `${currency}${v}`} tick={{ fill: '#9CA3AF', fontSize: 12 }} width={70} />
                <Tooltip
                  formatter={(v, name) => [formatCurrency(Number(v)), String(name) === 'periodA' ? t('analytics.periodA') : t('analytics.periodB')]}
                  contentStyle={{ background: 'var(--tooltip-bg, #fff)', color: 'var(--tooltip-text, #374151)', border: '1px solid var(--tooltip-border, #e5e7eb)', borderRadius: '8px', fontSize: '13px' }}
                />
                <Legend formatter={(value) => value === 'periodA' ? t('analytics.periodA') : t('analytics.periodB')} />
                <Area type="monotone" dataKey="periodA" stroke="#af7964" fill="url(#compareGradA)" strokeWidth={2} name="periodA" />
                <Area type="monotone" dataKey="periodB" stroke="#9CA3AF" fill="url(#compareGradB)" strokeWidth={2} name="periodB" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  TAB 9: SETTINGS
// ══════════════════════════════════════════════════════════════

function SettingsTab({ siteId, settings, t }: { siteId: string; settings: SiteAnalyticsSettings; t: ReturnType<typeof useTranslation>['t'] }) {
  const updateSettings = useUpdateAnalyticsSettings();
  const syncSettings = useSyncAnalyticsSettings();
  const [localSettings, setLocalSettings] = useState<SiteAnalyticsSettings>(settings);
  const [saved, setSaved] = useState(false);

  // Sync localSettings when server settings change (e.g. after save/sync invalidation)
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const hasChanges = JSON.stringify(localSettings) !== JSON.stringify(settings);
  const customCount = localSettings.allStatuses.filter((s) => s.isCustom).length;

  // Auto-sync from store when allStatuses is empty (first time opening settings)
  const autoSynced = useRef(false);
  const prevSiteId = useRef(siteId);
  useEffect(() => {
    if (prevSiteId.current !== siteId) {
      autoSynced.current = false;
      prevSiteId.current = siteId;
    }
    if (settings.allStatuses.length === 0 && !autoSynced.current && !syncSettings.isPending) {
      autoSynced.current = true;
      syncSettings.mutate(siteId, {
        onSuccess: (data) => {
          setLocalSettings(data);
        },
      });
    }
  }, [settings.allStatuses.length, siteId]);

  function handleSave() {
    updateSettings.mutate(
      { siteId, settings: localSettings },
      {
        onSuccess: () => {
          setSaved(true);
          setTimeout(() => setSaved(false), 3000);
        },
      },
    );
  }

  function handleSync() {
    syncSettings.mutate(siteId, {
      onSuccess: (data) => {
        setLocalSettings(data);
      },
    });
  }

  function togglePaid(slug: string) {
    setLocalSettings((prev) => {
      const newPaid = prev.paidStatuses.includes(slug)
        ? prev.paidStatuses.filter((s) => s !== slug)
        : [...prev.paidStatuses, slug];
      return {
        ...prev,
        paidStatuses: newPaid,
        allStatuses: prev.allStatuses.map((s) =>
          s.slug === slug ? { ...s, isPaid: !s.isPaid } : s,
        ),
      };
    });
  }

  return (
    <div className="space-y-6">
      {/* Date & Display */}
      <Card title={t('analytics.dateDisplay')}>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('analytics.defaultPreset')}</label>
            <select
              value={localSettings.defaultPreset}
              onChange={(e) => setLocalSettings((prev) => ({ ...prev, defaultPreset: e.target.value as AnalyticsDateRangePreset }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              <option value="today">{t('analytics.today')}</option>
              <option value="yesterday">{t('analytics.yesterday')}</option>
              <option value="last7days">{t('analytics.last7days')}</option>
              <option value="last14days">{t('analytics.last14days')}</option>
              <option value="last30days">{t('analytics.last30days')}</option>
              <option value="last90days">{t('analytics.last90days')}</option>
              <option value="last365days">{t('analytics.last365days')}</option>
              <option value="thisWeek">{t('analytics.thisWeek')}</option>
              <option value="lastWeek">{t('analytics.lastWeek')}</option>
              <option value="thisMonth">{t('analytics.thisMonth')}</option>
              <option value="lastMonth">{t('analytics.lastMonth')}</option>
              <option value="thisQuarter">{t('analytics.thisQuarter')}</option>
              <option value="thisYear">{t('analytics.thisYear')}</option>
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('analytics.compareDefaultPreset')}</label>
            <select
              value={localSettings.compareDefaultPreset}
              onChange={(e) => setLocalSettings((prev) => ({ ...prev, compareDefaultPreset: e.target.value as AnalyticsCompareMode }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              <option value="previousPeriod">{t('analytics.previousPeriod')}</option>
              <option value="previousYear">{t('analytics.previousYear')}</option>
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('analytics.dateColumnLabel')}</label>
            <select
              value={localSettings.dateColumn}
              onChange={(e) => setLocalSettings((prev) => ({ ...prev, dateColumn: e.target.value as SiteAnalyticsSettings['dateColumn'] }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              <option value="date_created">{t('analytics.dateCreated')}</option>
              <option value="date_paid">{t('analytics.datePaid')}</option>
              <option value="date_completed">{t('analytics.dateCompleted')}</option>
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('analytics.weekStartsOn')}</label>
            <select
              value={localSettings.weekStartsOn}
              onChange={(e) => setLocalSettings((prev) => ({ ...prev, weekStartsOn: Number(e.target.value) as 0 | 1 }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              <option value={1}>{t('analytics.monday')}</option>
              <option value={0}>{t('analytics.sunday')}</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Order Statuses */}
      <Card
        title={t('analytics.orderStatusesTitle')}
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={handleSync}
            disabled={syncSettings.isPending}
          >
            <ArrowPathIcon className={cn('mr-1.5 h-4 w-4', syncSettings.isPending && 'animate-spin')} />
            {syncSettings.isPending ? t('analytics.syncing') : t('analytics.syncFromStore')}
          </Button>
        }
      >
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          {t('analytics.paidStatusesHelp')}
        </p>
        {localSettings.allStatuses.length > 0 && (
          <p className="mb-3 text-xs text-gray-400 dark:text-gray-500">
            {t('analytics.syncedFromWc', { count: localSettings.allStatuses.length, custom: customCount })}
          </p>
        )}
        <div className="space-y-1">
          {localSettings.allStatuses.length > 0 ? (
            localSettings.allStatuses.map((status) => (
              <div key={status.slug} className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <div className="flex items-center gap-3">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[status.slug.replace('wc-', '')] ?? '#9CA3AF' }} />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{status.label}</span>
                  <span className="text-xs text-gray-400">{status.slug}</span>
                  {(status.slug === 'wc-processing' || status.slug === 'wc-completed') && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      {t('analytics.defaultStatus')}
                    </span>
                  )}
                  {status.isCustom && (
                    <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                      {t('analytics.customStatus')}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => togglePaid(status.slug)}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                    status.isPaid
                      ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600',
                  )}
                >
                  {status.isPaid ? t('analytics.paid') : t('analytics.unpaid')}
                </button>
              </div>
            ))
          ) : (
            <p className="py-4 text-center text-sm text-gray-400 dark:text-gray-500">
              {t('analytics.syncFromStore')}
            </p>
          )}
        </div>
      </Card>

      {/* Store Info */}
      <Card title={t('analytics.storeInfo')}>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('analytics.taxesEnabledLabel')}</p>
            <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
              {settings.taxesEnabled ? t('analytics.enabled') : t('analytics.disabled')}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('analytics.hposEnabledLabel')}</p>
            <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
              {settings.hposEnabled ? t('analytics.enabled') : t('analytics.disabled')}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('analytics.timezone')}</p>
            <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
              {settings.timezone || '-'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('analytics.lastSynced')}</p>
            <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
              {settings.lastSyncedAt ? formatRelativeTime(settings.lastSyncedAt) : t('analytics.never')}
            </p>
          </div>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={!hasChanges} loading={updateSettings.isPending}>
          {t('common.save')}
        </Button>
        {saved && (
          <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
            <CheckCircleIcon className="h-4 w-4" />
            {t('analytics.settingsSaved')}
          </span>
        )}
      </div>
    </div>
  );
}
